
import React from 'react';
import { Users, Calendar, Star, Zap } from 'lucide-react';
import { BrandCard, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/brand';
import { BrandText, BrandIcon } from '@/components/ui/brand';
import { Skeleton } from '@/components/ui/skeleton';
import { SubscriptionInfo } from '@/api/subscription/types';

interface DashboardMetricsGridProps {
  activeStaffCount: number;
  subscription: SubscriptionInfo | null;
  tasksCount: number;
  isStaffLoading: boolean;
  isSubscriptionLoading: boolean;
  isActivitiesLoading: boolean;
}

const DashboardMetricsGrid: React.FC<DashboardMetricsGridProps> = ({
  activeStaffCount,
  subscription,
  tasksCount,
  isStaffLoading,
  isSubscriptionLoading,
  isActivitiesLoading
}) => {
  const metrics = [
    {
      title: "Active AI Staff",
      value: activeStaffCount,
      icon: Users,
      description: "AI assistants working for you",
      loading: isStaffLoading,
      color: "primary"
    },
    {
      title: "Subscription Plan",
      value: subscription?.planName || "Free",
      icon: Star,
      description: subscription?.status || "Active",
      loading: isSubscriptionLoading,
      color: "secondary"
    },
    {
      title: "Tasks This Month",
      value: tasksCount,
      icon: Calendar,
      description: "Tasks completed by AI staff",
      loading: isActivitiesLoading,
      color: "primary"
    },
    {
      title: "Available Credits",
      value: subscription?.credits || 0,
      icon: Zap,
      description: "Credits available for AI tasks",
      loading: isSubscriptionLoading,
      color: "secondary"
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {metrics.map((metric, index) => (
        <BrandCard key={index} animation="fade-in" className="h-full">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
            <BrandIcon icon={metric.icon} color={metric.color as any} size="sm" background />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metric.loading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>{metric.value}</>
              )}
            </div>
            <BrandText size="sm" muted>
              {metric.loading ? (
                <Skeleton className="h-4 w-full mt-2" />
              ) : (
                metric.description
              )}
            </BrandText>
          </CardContent>
        </BrandCard>
      ))}
    </div>
  );
};

export default DashboardMetricsGrid;
